import { baseKeymap } from 'tiptap-commands';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { EditorState, Selection, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import { debounce } from 'lodash';
import ItemList from 'flarum/common/utils/ItemList';
import disabledPlugin from './plugins/disabledPlugin';
import disableBase64PastePlugin from './plugins/disableBase64PastePlugin';
import placeholderPlugin from './plugins/placeholderPlugin';
import menuPlugin from './plugins/menuPlugin';
import toggleSpoiler from './plugins/toggleSpoiler';
import richTextKeymap from './key-bindings';
import buildInputRules from './inputrules';
import MarkdownSerializerBuilder from './markdown/MarkdownSerializerBuilder';
import MarkdownParserBuilder from './markdown/MarkdownParserBuilder';
import SchemaBuilder from './markdown/SchemaBuilder';
import { inputRules } from 'prosemirror-inputrules';
import AlertUtil from 'flarum/forum/dianLiang/AlertUtil';

export default class ProseMirrorEditorDriver {
  constructor(target, attrs) {
    this.MAX_CHARACTERS = 10000; // 设置最大字符数为1万
    this.debouncedUpdateCharCount = debounce(this.updateCharCount.bind(this), 100); // 防抖时间为100毫秒
    this.build(target, attrs);
  }

  build(target, attrs) {
    this.attrs = attrs;
    this.schema = new SchemaBuilder().build();

    this.parser = new MarkdownParserBuilder(this.schema).build();
    this.serializer = new MarkdownSerializerBuilder(this.schema).build();

    this.state = EditorState.create(this.buildEditorStateConfig());
    this.view = new EditorView(target, this.buildEditorProps());

    const cssClasses = attrs.classNames || [];
    cssClasses.forEach((className) => this.view.dom.classList.add(className));

    // 添加字符计数器
    this.charCountElement = document.createElement('span');
    this.charCountElement.className = 'char-count';
    this.charCountElement.textContent = `${this.state.doc.textContent.length} / ${this.MAX_CHARACTERS}`;
    target.appendChild(this.charCountElement);

    const callInputListeners = (e) => {
      (this.attrs.inputListeners || []).forEach((listener) => {
        try {
          listener.call(target);
        } catch (err) {
          console.error('inputListener error', err);
        }
      });

      e.redraw = false;
    };

    target.oninput = callInputListeners;
    target.onclick = callInputListeners;
    target.onkeyup = callInputListeners;
  }

  buildEditorStateConfig() {
    return {
      doc: this.parseInitialValue(this.attrs.value),
      disabled: this.attrs.disabled,
      schema: this.schema,
      plugins: this.buildPluginItems().toArray(),
    };
  }

  buildPluginItems() {
    const items = new ItemList();

    items.add('markdownInputrules', inputRules({ rules: this.buildInputRules(this.schema) }));

    items.add('submit', keymap({ 'Mod-Enter': this.attrs.onsubmit }));

    items.add('escape', keymap({ Escape: this.attrs.escape }));

    items.add('richTextKeymap', keymap(richTextKeymap(this.schema)));

    items.add('baseKeymap', keymap(baseKeymap));

    items.add('placeholder', placeholderPlugin(this.attrs.placeholder));

    items.add('history', history());

    items.add('disabled', disabledPlugin());

    items.add('disableBase64Paste', disableBase64PastePlugin());

    items.add('dropCursor', dropCursor());

    items.add('gapCursor', gapCursor());

    items.add('menu', menuPlugin(this.attrs.menuState));

    items.add('toggleSpoiler', toggleSpoiler(this.schema));

    return items;
  }

  buildEditorProps() {
    const self = this;

    return {
      state: this.state,
      dispatchTransaction(transaction) {
        let newState = this.state.apply(transaction);
        // 递归函数来删除超过限制的字符
        function deleteExcessCharacters(state, maxLength) {
          let totalLength = 0;
          let deleteStartNode = null;
          let deleteStartPos = 0;
          let deleteStartOffset = 0;
          let deleteEndNode = null;
          let deleteEndPos = 0;
          let deleteEndOffset = 0;

          // 遍历文档结构，计算总长度并找到需要删除的起始和结束位置
          state.doc.descendants((node, pos) => {
            if (node.isText) {
              totalLength += node.text.length;
              if (totalLength > maxLength) {
                if (!deleteStartNode) {
                  deleteStartNode = node;
                  deleteStartPos = pos;
                  deleteStartOffset = maxLength - (totalLength - node.text.length);
                }
                deleteEndNode = node;
                deleteEndPos = pos;
                deleteEndOffset = node.text.length;
              }
            }
          });

          if (totalLength > maxLength) {
            try {
              const deleteStart = deleteStartPos + deleteStartOffset;
              const deleteEnd = deleteEndPos + deleteEndOffset;
              const tr = state.tr.delete(deleteStart, deleteEnd);
              const newState = state.apply(tr);
              // 提示用户
              AlertUtil.onWarn(app, app.translator.trans('dl-alert.forum.resopnse_rule'));
              // 递归调用，直到文档长度不再超过限制
              return deleteExcessCharacters(newState, maxLength);
            } catch (error) {
              console.error('Error deleting excess characters:', error);
              return state;
            }
          }
          return state;
        }
        // 调用递归函数
        newState = deleteExcessCharacters(newState, self.MAX_CHARACTERS);
        this.updateState(newState);
        const newDoc = this.state.doc;
        const newDocPlaintext = self.serializeContent(newDoc, self.schema);
        if (typeof self.attrs.oninput === 'function') self.attrs.oninput(newDocPlaintext);
        self.debouncedUpdateCharCount();
      },
    };
  }

  // 更新字符计数的方法
  updateCharCount() {
    const newDocLength = this.view.state.doc.textContent.length;
    this.charCountElement.textContent = `${newDocLength} / ${this.MAX_CHARACTERS}`;
  }

  buildInputRules(schema) {
    return buildInputRules(schema);
  }

  parseInitialValue(raw) {
    let value = raw;
    if (value === undefined || value === null) value = '';
    if (typeof value !== 'string') {
      if (typeof value === 'object') {
        if (typeof value.raw === 'string') value = value.raw;
        else if (typeof value.content === 'string') value = value.content;
        else if (typeof value.toString === 'function') value = value.toString();
        else value = '';
      } else {
        value = String(value);
      }
    }
    if (!value.trim()) {
      return this.schema.topNodeType.createAndFill();
    }

    try {
      // 压缩连续的换行符，将每组连续的换行符减少一个
      value = value.replace(/\n{2,}/g, (match) => {
        const newLines = match.length - 1; // 将换行符数量减少1
        return '\n'.repeat(newLines); // 返回新的换行符串
      });

      // 根据压缩后的换行符数量来分隔段落
      const parts = [];
      let lastIndex = 0;

      for (let i = 0; i < value.length; i++) {
        if (value[i] === '\n') {
          let count = 1;
          // 检查后续连续的换行符
          while (i + count < value.length && value[i + count] === '\n') {
            count++;
          }

          // 如果是两个或更多的换行符
          if (count >= 2) {
            parts.push(value.slice(lastIndex, i)); // 提取上一个段落
            lastIndex = i + count; // 更新段落起始位置
            i += count - 1; // 跳过多个换行符

            for (let j = 0; j < count - 1; j++) {
              parts.push(''); // 添加空段落
            }
          }
        }
      }

      // 处理最后一段文本
      if (lastIndex < value.length) {
        parts.push(value.slice(lastIndex));
      }

      // 将每个部分转换为段落节点
      const nodes = [];
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const trimmed = part.replace(/^\s+|\s+$/g, ''); // 去掉首尾空白

        if (trimmed.length > 0) {
          try {
            const partDoc = this.parser.parse(trimmed);
            partDoc.content.forEach((n) => nodes.push(n));
          } catch (e) {
            nodes.push(this.schema.nodes.paragraph.create(null, this.schema.text(trimmed)));
          }
        } else {
          // 空段落直接插入
          nodes.push(this.schema.nodes.paragraph.create());
        }
      }

      const doc = this.schema.topNodeType.create(null, nodes);
      return doc;
    } catch (e) {
      console.error('❌ Markdown parse failed in parseInitialValue:', e, 'input=', value);
      return this.schema.topNodeType.createAndFill();
    }
  }

  serializeContent(doc) {
    return this.serializer.serialize(doc, { tightLists: true });
  }

  // External Control Stuff

  /**
   * Focus the textarea and place the cursor at the given index.
   *
   * @param {number} position
   */
  moveCursorTo(position) {
    this.setSelectionRange(position, position);
  }

  /**
   * Get the selected range of the textarea.
   *
   * @return {Array}
   */
  getSelectionRange() {
    return [this.view.state.selection.from, this.view.state.selection.to];
  }

  /**
   * Get (at most) the last N characters from the current "text block".
   */
  getLastNChars(n) {
    const lastNode = this.view.state.selection.$from.nodeBefore;

    if (!lastNode || !lastNode.text) return '';

    return lastNode.text.slice(Math.max(0, lastNode.text.length - n));
  }

  /**
   * Insert content into the textarea at the position of the cursor.
   *
   * @param {String} text
   */
  insertAtCursor(text, escape) {
    this.insertAt(this.getSelectionRange()[0], text, escape);
    $(this.view.dom).trigger('click');
  }

  /**
   * Insert content into the textarea at the given position.
   *
   * @param {number} pos
   * @param {String} text
   */
  insertAt(pos, text, escape) {
    this.insertBetween(pos, pos, text, escape);
  }

  /**
   * Insert content into the textarea between the given positions.
   *
   * If the start and end positions are different, any text between them will be
   * overwritten.
   *
   * @param start
   * @param end
   * @param text
   * @param rawMarkdown
   */
  insertBetween(start, end, text, escape = true) {
    let trailingNewLines = 0;

    const OFFSET_TO_REMOVE_PREFIX_NEWLINE = 0;

    if (escape) {
      this.view.dispatch(this.view.state.tr.insertText(text, start, end));
    } else {
      // Without this, a newline would be added before the inserted text.
      start -= OFFSET_TO_REMOVE_PREFIX_NEWLINE;
      const parsedText = this.parseInitialValue(text);
      this.view.dispatch(this.view.state.tr.replaceRangeWith(start, end, parsedText));

      const m = text.match(/\s+$/);
      trailingNewLines = m ? m[0].split('\n').length - 1 : 0;
    }

    // Move the textarea cursor to the end of the content we just inserted.
    // The offset is necessary so the new cursor position doesn't split the inserted text
    // when the space is added below.
    this.moveCursorTo(Math.min(start + text.length + 1, Selection.atEnd(this.view.state.doc).to));
    m.redraw();

    // TODO: accomplish this in one step.
    if (text.endsWith(' ') && !escape) {
      this.insertAtCursor(' ');
    }

    Array(trailingNewLines)
      .fill(0)
      .forEach(() => {
        baseKeymap['Enter'](this.view.state, this.view.dispatch);
      });
  }

  /**
   * Replace existing content from the start to the current cursor position.
   *
   * @param start
   * @param text
   */
  replaceBeforeCursor(start, text, escape) {
    this.insertBetween(start, this.getSelectionRange()[0], text, escape);
  }

  /**
   * Set the selected range of the textarea.
   *
   * @param {number} start
   * @param {number} end
   * @private
   */
  setSelectionRange(start, end) {
    const $start = this.view.state.tr.doc.resolve(start);
    const $end = this.view.state.tr.doc.resolve(end);

    this.view.dispatch(this.view.state.tr.setSelection(new TextSelection($start, $end)));
    this.focus();
  }

  getCaretCoordinates(position) {
    const viewportCoords = this.view.coordsAtPos(position);
    const editorViewportOffset = this.view.dom.getBoundingClientRect();
    return {
      left: viewportCoords.left - editorViewportOffset.left,
      top: viewportCoords.top - editorViewportOffset.top,
    };
  }

  focus() {
    this.view.focus();
  }

  destroy() {
    this.view.destroy();
  }

  disabled(disabled) {
    this.view.dispatch(this.view.state.tr.setMeta('disabled', disabled));
  }
}
